import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Button,
  Chip,
  Stack,
  styled,
} from "@mui/material";
import { motion } from "framer-motion";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import BgTitle from "components/title/BgTitle";
import SalaryCalculatorCTA from "components/Recruitment/SalaryCalculatorCTA";
import { SHOW_SALARY_ESTIMATOR } from "config/featureFlags";
import newGradBgImg from "images/recruitment/new-grad-recruitment.jpg";
import midBgImg from "images/recruitment/mid-level-recruitment.png";

const ContactInfo = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
}));

const InfoItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
  color: theme.palette.text.secondary,
  "&:last-child": {
    marginBottom: 0,
  },
}));

const JobCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  overflow: "hidden",
  borderRadius: theme.spacing(2),
  border: "1px solid rgba(0, 0, 0, 0.08)",
  backgroundColor: "#f5f5f5",
  height: "100%", // Ensure all cards have the same height
  display: "flex",
  flexDirection: "column",
}));

const CardContentStyled = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  flexGrow: 1, // Allow content to expand
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  backgroundColor: "#e1e1e194",
  color: "black",
  fontWeight: 550,
  fontSize: "0.7rem",
}));

const ActionButton = styled(Button)(({ theme }) => ({
  marginTop: "auto", // Push button to bottom
  textTransform: "none",
  fontWeight: 500,
  borderRadius: theme.spacing(1.5),
  fontSize: "0.8rem",
  backgroundColor: "#0c4d96",
  "&:hover": {
    backgroundColor: "#083a6e",
  },
}));

const images = [newGradBgImg, midBgImg];
export default () => {
  const { t } = useTranslation();
  const cards = t("recruitment.cards", { returnObjects: true });

  return (
    <Container maxWidth="sm" sx={{ py: 3 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header Section */}
        <BgTitle text={t("recruitment.subheading")} />
        <Box
          sx={{
            bgcolor: "#f5f5f5",
            borderLeft: "4px solid",
            borderColor: "primary.main",
            p: 1,
            mt: 2,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              mt: 2,
              color: "text.secondary",
              fontSize: "0.8rem",
              lineHeight: 1.6,
            }}
          >
            {t("recruitment.description.subtitle")}
          </Typography>
        </Box>

        {/* Contact Information */}
        <ContactInfo>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              mb: 2,
              color: "text.primary",
            }}
          >
            {t("recruitment.description.contact.title")}
          </Typography>
          <InfoItem>
            <PhoneIcon sx={{ fontSize: 18 }} />
            <Typography variant="body2">Tel: 03-5828-6441</Typography>
          </InfoItem>
          <InfoItem>
            <EmailIcon sx={{ fontSize: 18 }} />
            <Typography variant="body2">Fax: 03-5828-6510</Typography>
          </InfoItem>
        </ContactInfo>

        {/* Job Cards */}
        <Box sx={{ mt: 4 }}>
          {cards.map((card, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <JobCard>
                <CardMedia
                  component="img"
                  height="160"
                  image={images[index]}
                  alt={card.title}
                  sx={{ objectFit: "cover" }}
                />
                <CardContentStyled>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        fontSize: "0.7rem",
                      }}
                    >
                      {card.company}
                    </Typography>
                    <StyledChip label={card.type} size="small" />
                  </Stack>

                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      mb: 2,
                      color: "text.primary",
                    }}
                  >
                    {card.title}
                  </Typography>

                  <Stack direction="row" spacing={3} mb={2}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <AccessTimeIcon
                        sx={{ fontSize: 16, color: "text.secondary" }}
                      />
                      <Typography
                        variant="body2"
                        fontSize={"0.7rem"}
                        color="text.secondary"
                      >
                        {card.durationText}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <LocationOnIcon
                        sx={{ fontSize: 16, color: "text.secondary" }}
                      />
                      <Typography
                        variant="body2"
                        fontSize={"0.7rem"}
                        color="text.secondary"
                      >
                        {card.locationText}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Push this to the bottom with marginTop: auto */}
                  {index === 0 ? (
                    <ActionButton
                      variant="contained"
                      fullWidth
                      endIcon={<ArrowForwardIcon />}
                      href={card.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Apply for ${card.title}`}
                    >
                      {t("recruitment.linkText")}
                    </ActionButton>
                  ) : (
                    <Stack spacing={1} sx={{ mt: "auto" }}>
                      <ActionButton
                        variant="contained"
                        fullWidth
                        endIcon={<ArrowForwardIcon />}
                        href={`mailto:${t("footer_contact_email_soumu")}`}
                        aria-label="Apply via email"
                        sx={{ mt: 0 }}
                      >
                        {t("recruitment_cards.normal_entry")}
                      </ActionButton>
                      <ActionButton
                        variant="outlined"
                        fullWidth
                        disabled
                        aria-disabled="true"
                        sx={{ mt: 0 }}
                      >
                        {t("recruitment_cards.line_entry")} (
                        {t("recruitment_cards.preparing")})
                      </ActionButton>
                    </Stack>
                  )}
                </CardContentStyled>
              </JobCard>
            </motion.div>
          ))}
        </Box>

        {/* Salary Calculator CTA Section */}
        {SHOW_SALARY_ESTIMATOR && (
          <Box sx={{ mt: 4 }}>
            <SalaryCalculatorCTA />
          </Box>
        )}
      </motion.div>
    </Container>
  );
};
